import { gql } from "@apollo/client";
import {
  PaymentMethodNoNestingFragment,
  PayTypeFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  LinklyConfigurationFragment,
  LinklyPairedPinpadNoNestingFragment,
  DocketFilterNoNestingFragment,
  DocketTemplateNoNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";

export const CREATE_PAYMENT_METHOD_MUTATION = gql`
  ${PaymentMethodNoNestingFragment}
  mutation CreatePaymentMethod($input: CreatePaymentMethodInput!) {
    createPaymentMethod(input: $input) {
      ...PaymentMethodNoNesting
    }
  }
`;

export const MUTATION_UPDATE_PAYMENT_METHOD = gql`
  ${PaymentMethodNoNestingFragment}
  mutation UpdatePaymentMethod($input: UpdatePaymentMethodInput!) {
    updatePaymentMethod(input: $input) {
      ...PaymentMethodNoNesting
    }
  }
`;

export const QUERY_LINKLY_CONFIGURATION = gql`
  ${LinklyConfigurationFragment}
  ${LinklyPairedPinpadNoNestingFragment}
  query QueryLinklyConfiguration($paymentMethodId: ID!) {
    linklyConfiguration(paymentMethodId: $paymentMethodId) {
      ...LinklyConfiguration
    }
  }
`;

export const MUTATION_UPDATE_LINKLY_CONFIGURATION = gql`
  ${LinklyConfigurationFragment}
  ${LinklyPairedPinpadNoNestingFragment}
  mutation MutationUpdateLinklyConfiguration(
    $paymentMethodId: ID!
    $input: LinklyConfigurationInput!
  ) {
    updateLinklyConfiguration(
      paymentMethodId: $paymentMethodId
      input: $input
    ) {
      ...LinklyConfiguration
    }
  }
`;

export const MUTATION_LINKLY_PAIR_PINPAD = gql`
  ${LinklyPairedPinpadNoNestingFragment}
  mutation MutationLinklyPairPinpad(
    $paymentMethodId: ID!
    $input: LinklyPairPinpadInput!
  ) {
    pairPinpad(paymentMethodId: $paymentMethodId, input: $input) {
      ... on LinklyPairedPinpad {
        ...LinklyPairedPinpadNoNesting
      }
      ... on LinklyError {
        errorCode
        message
      }
      ... on InvalidLinklyCredentialsError {
        errorCode
        message
      }
    }
  }
`;

export const MUTATION_REMOVE_PINPAD = gql`
  mutation MutationRemovePinpad($pinpadId: ID!) {
    removePinpad(pinpadId: $pinpadId) {
      success
    }
  }
`;

export const MUTATION_UPDATE_DOCKET_TEMPLATE = gql`
  ${DocketTemplateNoNestingFragment}
  ${DocketFilterNoNestingFragment}
  mutation UpdateDocketTemplate($input: UpdateDocketTemplateInput!) {
    updateDocketTemplate(input: $input) {
      ...DocketTemplateNoNesting
      filters {
        ...DocketFilterNoNesting
      }
    }
  }
`;
export const MUTATION_CREATE_DOCKET_TEMPLATE = gql`
  ${DocketTemplateNoNestingFragment}
  ${DocketFilterNoNestingFragment}
  mutation CreateDocketTemplate($input: CreateDocketTemplateInput!) {
    createDocketTemplate(input: $input) {
      ...DocketTemplateNoNesting
      filters {
        ...DocketFilterNoNesting
      }
    }
  }
`;
export const MUTATION_DELETE_DOCKET_TEMPLATE = gql`
  mutation DeleteDocketTemplate($id: ID!) {
    deleteDocketTemplate(id: $id)
  }
`;

export const QUERY_PAYMENT_METHODS = gql`
  ${PaymentMethodNoNestingFragment}
  ${PayTypeFragment}
  query paymentMethods {
    paymentMethods(options: { limit: 1000 }) {
      items {
        ...PaymentMethodNoNesting
        paytype {
          ...PayType
        }
      }
    }
  }
`;

export const QUERY_PAYMENT_METHOD = gql`
  ${PaymentMethodNoNestingFragment}
  ${PayTypeFragment}
  query paymentMethod($id: ID!) {
    paymentMethod(id: $id) {
      ...PaymentMethodNoNesting
      paytype {
        ...PayType
      }
    }
  }
`;

export const MUTATION_CREATE_PAYMENT_METHOD = gql`
  ${PaymentMethodNoNestingFragment}
  ${PayTypeFragment}
  mutation createPaymentMethod($input: CreatePaymentMethodInput!) {
    createPaymentMethod(input: $input) {
      ...PaymentMethodNoNesting
      paytype {
        ...PayType
      }
    }
  }
`;

export const QUERY_STAFF_USERS_LIST = gql`
  query staffUsersList($options: StaffListOptions) {
    staffs(options: $options) {
      items {
        id
        userId
        initials
        enabled
        username
        fullName
        emailAddress
      }
      meta {
        itemCount
        totalItems
        currentPage
      }
    }
  }
`;

export const QUERY_STAFF_USER = gql`
  query staff($id: ID!) {
    staff(id: $id) {
      id
      userId
      user {
        mfaMethods {
          identifier
          strategy
          verified
        }
      }
      initials
      enabled
      username
      fullName
      emailAddress
    }
  }
`;

export const MUTATION_CREATE_STAFF_WITH_USER_ACCOUNT = gql`
  mutation createStaffWithUserAccount(
    $input: CreateStaffWithUserAccountInput!
  ) {
    createStaffWithUserAccount(input: $input) {
      id
      userId
      initials
      enabled
      username
      fullName
      emailAddress
    }
  }
`;

export const MUTATION_UPDATE_STAFF_USER_ACCOUNT = gql`
  mutation updateStaffUserAccount($input: UpdateStaffUserAccountInput!) {
    updateStaffUserAccount(input: $input) {
      id
      userId
      initials
      enabled
      username
      fullName
      emailAddress
    }
  }
`;

export const MUTATION_UPGRADE_STAFF_TO_USER_ACCOUNT = gql`
  mutation createStaffUserAccount($input: CreateStaffUserAccountInput!) {
    createStaffUserAccount(input: $input) {
      id
      userId
      initials
      enabled
      username
      fullName
      emailAddress
    }
  }
`;

export const QUERY_LIST_RECEIPT_TEMPLATES = gql`
  query receipts($options: ReceiptListOptions) {
    receipts(options: $options) {
      items {
        id
        name
        defaultTemplateType
        settings {
          header
          name
          footer
          displayCustomerDetails
          logoURL
          labelTax
        }
        enabled
      }
      meta {
        itemCount
        totalItems
        currentPage
      }
    }
  }
`;

export const QUERY_RECEIPT_TEMPLATE = gql`
  query receipt($id: ID!) {
    receipt(id: $id) {
      id
      name
      defaultTemplateType
      settings {
        header
        name
        footer
        displayCustomerDetails
        logoURL
        labelTax
      }
      enabled
    }
  }
`;

export const MUTATION_UPDATE_RECEIPT_TEMPLATE = gql`
  mutation updateReceipt($input: ReceiptInput!) {
    updateReceipt(input: $input) {
      id
      name
      defaultTemplateType
      settings {
        header
        name
        footer
        displayCustomerDetails
        logoURL
        labelTax
      }
      enabled
    }
  }
`;

export const MUTATION_CREATE_RECEIPT_TEMPLATE = gql`
  mutation createReceipt($input: ReceiptInput!) {
    createReceipt(input: $input) {
      id
      name
      defaultTemplateType
      settings {
        header
        name
        footer
        displayCustomerDetails
        logoURL
        labelTax
      }
      enabled
    }
  }
`;

export const QUERY_LOCATIONS = gql`
  query locations($options: LocationListOptions) {
    locations(options: $options) {
      items {
        id
        name
        enabled
        retailerLocationKey
      }
      meta {
        itemCount
        totalItems
        currentPage
      }
    }
  }
`;

export const QUERY_LOCATION = gql`
  query location($id: ID!) {
    location(id: $id) {
      id
      name
      enabled
      retailerLocationKey
    }
  }
`;

export const MUTATION_UPDATE_LOCATION = gql`
  mutation updateLocation($input: UpdateLocationInput!) {
    updateLocation(input: $input) {
      id
      name
      enabled
      retailerLocationKey
    }
  }
`;

export const QUERY_TERMINALS = gql`
  query terminals($options: TerminalListOptions) {
    terminals(options: $options) {
      items {
        id
        name
        invoiceSequence
        saleSufix
        salePrefix
        enabled
        paymentMethodIds
        defaultReceiptTemplateId
        printReceiptOnCheckout
      }
      meta {
        itemCount
        totalItems
        currentPage
      }
    }
  }
`;

export const QUERY_TERMINAL = gql`
  query terminal($id: ID!) {
    terminal(id: $id) {
      id
      name
      invoiceSequence
      saleSufix
      salePrefix
      enabled
      paymentMethodIds
      defaultReceiptTemplateId
      printReceiptOnCheckout
    }
  }
`;

export const MUTATION_UPDATE_TERMINAL = gql`
  mutation updateTerminal($input: UpdateTerminalInput!) {
    updateTerminal(input: $input) {
      id
      name
      invoiceSequence
      saleSufix
      salePrefix
      enabled
      paymentMethodIds
      defaultReceiptTemplateId
      printReceiptOnCheckout
    }
  }
`;

export const QUERY_ACCESS_TOKENS = gql`
  query accessTokens {
    accessTokens {
      createdAt
      expires
      id
      invalidated
      name
      token
      userId
    }
  }
`;

export const QUERY_ACCESS_TOKEN = gql`
  query accessToken($id: ID!) {
    accessToken(id: $id) {
      createdAt
      expires
      id
      invalidated
      name
      token
      userId
    }
  }
`;

export const MUTATION_GENERATE_ACCESS_TOKEN = gql`
  mutation generateAccessToken($input: GenerateAccessTokenInput!) {
    generateAccessToken(input: $input) {
      createdAt
      expires
      id
      invalidated
      name
      token
      userId
    }
  }
`;

export const MUTATION_DELETE_ACCESS_TOKEN = gql`
  mutation deleteAccessToken($id: ID!) {
    deleteAccessToken(id: $id) {
      result
    }
  }
`;

export const QUERY_LLM_STRATEGIES = gql`
  query aiStrategies($options: AiStrategyListOptions) {
    aiStrategies(options: $options) {
      items {
        id
        name
        aiType
        config {
          code
          args {
            value
            name
          }
        }
      }
      meta {
        itemCount
        totalItems
        currentPage
      }
    }
  }
`;

export const QUERY_QUICK_WIN_HANDLERS = gql`
  query quickWinHandlers {
    quickWinHandlers {
      code
      name
      description
      args {
        defaultValue
        name
        description
      }
    }
  }
`;

export const QUERY_QUICK_WIN_STRATEGY_BY_CODE = gql`
  query quickWinStrategyByCode($code: String!) {
    quickWinStrategyByCode(code: $code) {
      enabled
      code
      config {
        code
        args {
          value
          name
        }
      }
      handler {
        code
        name
        description
        args {
          defaultValue
          name
          description
        }
      }
    }
  }
`;

export const MUTATION_UPDATE_QUICK_WIN_STRATEGY = gql`
  mutation updateQuickWinStrategy($input: UpdateQuickWinStrategyInput!) {
    updateQuickWinStrategy(input: $input) {
      enabled
      config {
        code
        args {
          value
          name
        }
      }
      handler {
        code
        name
        description
      }
    }
  }
`;

export const QUERY_LLM_STRATEGY_BY_ID = gql`
  query aiStrategy($id: ID!) {
    aiStrategy(id: $id) {
      id
      name
      aiType
      config {
        code
        args {
          value
          name
        }
      }
    }
  }
`;

export const MUTATION_CREATE_LLM_STRATEGY = gql`
  mutation createAiStrategy($input: CreateAiStrategyInput!) {
    createAiStrategy(input: $input) {
      id
      name
      aiType
      config {
        code
        args {
          value
          name
        }
      }
    }
  }
`;

export const MUTATION_UPDATE_LLM_STRATEGY = gql`
  mutation updateAiStrategy($input: UpdateAiStrategyInput!) {
    updateAiStrategy(input: $input) {
      id
      name
      aiType
      config {
        code
        args {
          value
          name
        }
      }
    }
  }
`;

export const DELETE_AI_STRATEGY_MUTATION = gql`
  mutation deleteAiStrategy($id: ID!) {
    deleteAiStrategy(id: $id) {
      result
    }
  }
`;

export const ACCEPT_AI_TERMS_MUTATION = gql`
  mutation acceptAiTerms {
    acceptAiTerms
  }
`;
